// The only way the frontend talks to the backend: fetch() against the
// same-origin /api/* path, with the browser's Secure/HttpOnly session
// cookie sent automatically (credentials: 'include' is same-origin-safe
// here since the API is same-origin, not cross-site). No token is ever
// read or stored by JavaScript — the cookie is HttpOnly by design, so this
// client cannot access it even if it wanted to.

import { appConfig } from './env';
import { timedFetch } from './telemetry';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
}

export async function apiRequest<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const method = options.method ?? 'GET';
  const response = await timedFetch(path, method, () =>
    fetch(`${appConfig.apiBaseUrl}${path}`, {
      method,
      credentials: 'include',
      headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined,
    }),
  );

  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    // Non-JSON response (e.g. an unexpected upstream error page); handled below.
  }

  if (!response.ok) {
    const body = (data && typeof data === 'object' ? data : {}) as { message?: unknown; error?: unknown };
    const message = body.message ? String(body.message) : `Request failed (${response.status})`;
    const code = body.error ? String(body.error) : undefined;
    throw new ApiError(message, response.status, code);
  }

  return data as TResponse;
}
